import { Injectable } from '@angular/core';

export interface CookieConsentPreferences {
    analytics: boolean;
    marketing: boolean;
}

interface StoredConsent extends CookieConsentPreferences {
    timestamp: number;
}

/** GDPR-style cookie consent persisted in localStorage. */
@Injectable({
    providedIn: 'root',
})
export class CookieConsentService {
    private static readonly CONSENT_KEY = 'saideline_cookie_consent';

    constructor() {
        this.applyStoredConsent();
    }

    /** Whether the user has stored cookie preferences. */
    hasConsent(): boolean {
        return this.getStoredConsent() !== null;
    }

    /**
     * Persists consent choices and loads enabled third-party scripts.
     * @param preferences - Analytics and marketing opt-in flags
     */
    setConsent(preferences: CookieConsentPreferences): void {
        const consent: StoredConsent = {
            ...preferences,
            timestamp: Date.now(),
        };
        localStorage.setItem(CookieConsentService.CONSENT_KEY, JSON.stringify(consent));
        this.applyConsent(preferences);
    }

    private getStoredConsent(): StoredConsent | null {
        try {
            const raw = localStorage.getItem(CookieConsentService.CONSENT_KEY);
            if (!raw) return null;
            return JSON.parse(raw) as StoredConsent;
        } catch {
            return null;
        }
    }

    private applyStoredConsent(): void {
        const consent = this.getStoredConsent();
        if (consent) {
            this.applyConsent(consent);
        }
    }

    private applyConsent(preferences: CookieConsentPreferences): void {
        if (preferences.analytics) {
            this.loadAnalytics();
        }
        if (preferences.marketing) {
            this.loadMarketing();
        }
    }

    private loadAnalytics(): void {
        if (document.getElementById('gtag-script')) return;

        const script = document.createElement('script');
        script.id = 'gtag-script';
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=G-4RQ558J8LX';
        document.head.appendChild(script);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const w = window as any;
        w.dataLayer = w.dataLayer || [];
        function gtag(...args: unknown[]): void {
            w.dataLayer.push(args);
        }
        w.gtag = gtag;
        gtag('js', new Date());
        gtag('config', 'G-4RQ558J8LX');
    }

    private loadMarketing(): void {
        if (document.getElementById('rewardful-script')) return;

        const inline = document.createElement('script');
        inline.textContent = `(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');`;
        document.head.appendChild(inline);

        const script = document.createElement('script');
        script.id = 'rewardful-script';
        script.async = true;
        script.src = 'https://r.wdfl.co/rw.js';
        script.setAttribute('data-rewardful', 'a2e480');
        document.head.appendChild(script);
    }
}
